import { useEffect, useRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomEditable } from 'src/components/RichSlate';
import { focusEditor, overrideContent } from 'src/components/RichSlate';
import { isMultiPromptTool } from 'src/data/generationTool';
import { useSlateContentSelectionRect } from 'src/lib/hooks/useSlateContentSelectionRect';
import { EMPTY_PARAGRAPH_HTML } from 'src/lib/slate';

import { ContinueRowFloatingPanel } from './components/ContinueRowFloatingPanel';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const PLACEHOLDER = 'Tell us what to write ';
const INITIAL_EDITOR_VALUE = EMPTY_PARAGRAPH_HTML;

export const InstructionsTextarea = ({
  onMenuButtonClick,
  setWasOpened,
  className,
  editor,
  value,
  onMenuOpen,
  onCloseMenu,
  classes,
  isSuggestPromptLoading,
  freestyleToolContentId,
  onKeyDown,
  generationTool,
  isSavePopup,
  onEditorFocus,
  setMenuType,
  shortExpanding,
  noTags,
  placeHolder,
  renderPlaceholder,
  footerShown,
  promptImages,
  plusButtonRef,
  setPlusButtonRef,
  isFirstField,
  noMinMaxHeight,
  cachedValue,
  onQuickSearchTriggered,
  disabled,
  hideContinueRowFloatingPanel,
  disableAutoFocus,
  disableKeyUpAction,
}) => {
  const styles = useStyles({
    classes,
    shortExpanding,
    footerShown,
    promptImages,
    noMinMaxHeight,
  });

  const editorRef = useRef();

  const { handleKeyUp, handlePaste, handleAddClick, handleTextAreaClick } = useHandlers({
    editor,
    onMenuOpen,
    onCloseMenu,
    setMenuType,
    noTags,
    onQuickSearchTriggered,
    onMenuButtonClick,
    setWasOpened,
    onEditorFocus,
    setPlusButtonRef,
    editorRef,
  });

  const selectionRect = useSlateContentSelectionRect(20);

  useEffect(() => {
    if (isFirstField) {
      setPlusButtonRef?.(editorRef?.current);
    }
  }, [isFirstField, setPlusButtonRef]);

  useEffect(() => {
    overrideContent(editor, freestyleToolContentId ? cachedValue || value : value || cachedValue);
    if (isMultiPromptTool(generationTool)) {
      return;
    }

    if (!disableAutoFocus) {
      focusEditor({ editor, atEnd: true });
    }
    // We are not adding 'value' and here so we won't run the useEffect on each character change in the
    // slate editor, only when the whole prompt change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, freestyleToolContentId, disableAutoFocus]);

  useEffect(() => {
    if (value === INITIAL_EDITOR_VALUE) {
      overrideContent(editor, value);
      if (!disableAutoFocus) {
        focusEditor({ editor, atEnd: true });
      }
    }
  }, [value, editor, disableAutoFocus]);

  useEffect(() => {
    if (!isSuggestPromptLoading && value && editor) {
      overrideContent(editor, value);
      if (!disableAutoFocus) {
        focusEditor({ editor, atEnd: true });
      }
    }
    // This is a special case where we want to run the useEffect only when the suggest prompt is finished loading
    // We are not adding 'value' so we won't run the useEffect on each character change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isSuggestPromptLoading, disableAutoFocus]);

  useEffect(() => {
    if (isSavePopup) {
      return;
    }
    overrideContent(editor, INITIAL_EDITOR_VALUE);
  }, [editor, generationTool, isSavePopup]);

  useEffect(() => {
    if (cachedValue) {
      overrideContent(editor, cachedValue);
      if (!disableAutoFocus) {
        focusEditor({ editor, atEnd: true });
      }
    }
  }, [generationTool, cachedValue, editor, disableAutoFocus]);

  return (
    <div
      className={cx(styles.root, className, { [styles.disabled]: disabled })}
      onClick={handleTextAreaClick}
      ref={editorRef}
    >
      {!hideContinueRowFloatingPanel && (!plusButtonRef || editorRef?.current === plusButtonRef) ? (
        <ContinueRowFloatingPanel
          selectionRect={selectionRect}
          onAddClick={handleAddClick}
        />
      ) : (
        <></>
      )}
      <CustomEditable
        className={styles.editable}
        onKeyUp={disableKeyUpAction ? null : handleKeyUp}
        placeholder={placeHolder || PLACEHOLDER}
        renderPlaceholder={renderPlaceholder}
        onKeyDown={onKeyDown}
        onPaste={handlePaste}
        data-testid="promptTextBox"
        readOnly={disabled}
      />
    </div>
  );
};

InstructionsTextarea.propTypes = {
  setWasOpened: PropTypes.func.isRequired,
  onMenuButtonClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  editor: PropTypes.shape().isRequired,
  value: PropTypes.string,
  onMenuOpen: PropTypes.func.isRequired,
  onCloseMenu: PropTypes.func.isRequired,
  classes: PropTypes.shape(),
  isSuggestPromptLoading: PropTypes.bool,
  freestyleToolContentId: PropTypes.string,
  onKeyDown: PropTypes.func.isRequired,
  generationTool: PropTypes.string,
  isSavePopup: PropTypes.bool,
  onEditorFocus: PropTypes.func,
  setMenuType: PropTypes.func.isRequired,
  shortExpanding: PropTypes.bool,
  noTags: PropTypes.bool,
  placeHolder: PropTypes.string,
  renderPlaceholder: PropTypes.func,
  footerShown: PropTypes.bool,
  setPlusButtonRef: PropTypes.shape(),
  plusButtonRef: PropTypes.shape(),
  isFirstField: PropTypes.bool,
  noMinMaxHeight: PropTypes.bool,
  promptImages: PropTypes.shape(),
  cachedValue: PropTypes.string,
  onQuickSearchTriggered: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hideContinueRowFloatingPanel: PropTypes.bool,
  disableAutoFocus: PropTypes.bool,
  disableKeyUpAction: PropTypes.bool,
};

InstructionsTextarea.defaultProps = {
  className: null,
  value: undefined,
  classes: null,
  isSuggestPromptLoading: false,
  freestyleToolContentId: '',
  generationTool: '',
  isSavePopup: false,
  onEditorFocus: null,
  shortExpanding: false,
  noTags: false,
  placeHolder: '',
  renderPlaceholder: undefined,
  footerShown: false,
  setPlusButtonRef: null,
  plusButtonRef: null,
  isFirstField: false,
  noMinMaxHeight: false,
  promptImages: null,
  cachedValue: undefined,
  disabled: false,
  hideContinueRowFloatingPanel: false,
  disableAutoFocus: false,
  disableKeyUpAction: false,
};
